import {Dropdown, Text} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {Condition, PromotionCondition} from '@/types/promotion'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_BUY_OPTION, ANY_ID} from '@/utils/constants/promotionConstants'
import {DropdownItem} from 'pepsico-ds'
import {FC, useMemo, useState} from 'react'
import PromotionSelectionModal from '../PromotionSelectionModal'
import {RewardItem} from './Rewards/RewardItem'
import {useRewardValidations} from './Rewards/RewardValidations'

type BuySingleProductConditionsProps = {
  promotionCondition: PromotionCondition
  index: number
}

export const BuySingleProductConditions: FC<BuySingleProductConditionsProps> = ({promotionCondition, index}) => {
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasBuyQuantityLayerError} = useRewardValidations(promotionCondition)
  let buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []

  const [modalProps, setModalProps] = useState<{type: string | ''; show: boolean}>({show: false, type: ''})

  const handleBuyCount = (values: DropdownItem[]) => {
    const value = values?.[0]?.id
    if (value) {
      // If ANY is selected in the quantity dropdown, set quantity to 1
      if (isNaN(value) && value.toLowerCase() === ANY_ID.toLowerCase()) {
        updateCurrentCondition({quantity: 1})
      } else {
        updateCurrentCondition({quantity: Number(value)})
      }
    }
    else {
      updateCurrentCondition({quantity: null})
    }
  }

  const updateCurrentCondition = (update: Partial<Condition>) => {
    updatePromotion(prev => {
      const updatedConditions = prev.promotionConditions.map(condition => {
        if (condition.sequence !== promotionCondition.sequence) return condition

        const updatedConditionGroup = {
          ...condition.conditionGroup,
          conditions: (condition.conditionGroup?.conditions || []).map((item, index) => {
            return index === 0 ? {...item, ...update} : item
          }),
        }

        return {
          ...condition,
          conditionGroup: updatedConditionGroup,
        }
      })

      return {
        ...prev,
        promotionConditions: updatedConditions,
      }
    })
  }

  const clearValues = () => {
    updateCurrentCondition({items: [], itemType: undefined})
  }

  const handleCloseModal = () => {
    setModalProps({type: '', show: false})
  }

  const handleValues = (selectedItemsAndType, productCombination) => {
    const {values, itemType} = selectedItemsAndType || {}
    if (!values || !Array.isArray(values)) {
      clearValues()
      return
    }
    const items = values.map(item => ({id: item?.id, name: item?.displayText}))

    updateCurrentCondition({items, itemType, productCombination})
    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            itemType: itemType,
            items: items,
          })),
        },
      })),
    }))
  }

  const handleSubmitModal = (selectedItems, combination) => {
    handleValues(selectedItems, combination)
    handleCloseModal()
  }

  const buyCount = promotionCondition?.conditionGroup?.conditions?.[0]?.quantity
  let buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const currentCondition = promotionCondition?.conditionGroup?.conditions?.[0]

  const items = currentCondition?.items ?? []

  // Use selectedItems for display if available, otherwise fall back to items
  const selectedValuesText = items.map(item => item.name).join(', ')
  const quantityValue = buyQtyOptions?.filter(item => item?.id === buyCount) ?? []

  // Calculate productsValue directly without state
  const productsValue = items.map(item => ({id: item.id, displayText: item.name})) ?? []

  const isAnySelected = currentCondition?.items?.find(item => item.id === ANY_ID)
  const handleSelectedBuyItem = (values: DropdownItem[]) => {
    if (!values || !values.length) {
      clearValues()
      return
    }

    const isModalValue = values.find(item => buyTypeOptions?.find(({id}) => id === item?.id))

    let updatedItemType = currentCondition?.itemType
    let updatedValues = values

    if (isModalValue) {
      if (isModalValue?.id === ANY_ID) {
        updatedItemType = ANY_ID
        updatedValues = [ANY_BUY_OPTION]
        handleValues({values: updatedValues, itemType: updatedItemType}, null)
      } else {
        setModalProps({show: true, type: isModalValue?.id})
        return // stop execution if modal opens
      }
    } else {
      // For regular items
      handleValues({values, itemType: updatedItemType}, null)
    }

    // 🔥 Update all sequences based on sequence 1 change
    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            itemType: updatedItemType,
            items: updatedValues,
          })),
        },
      })),
    }))
  }

  const buyQtyOptionsData = new Set(quantityValue?.map(item => item?.id))

  if (buyQtyOptionsData?.has('any')) {
    buyTypeOptions = buyTypeOptions?.filter(item => item?.id !== ANY_ID)
  }
  if (isAnySelected) {
    buyQtyOptions = buyQtyOptions?.filter(item => item?.id !== ANY_ID)
  }

  const isFirstCondition = promotionCondition?.sequence === 1
  const isError = hasBuyQuantityLayerError()

  return (
    <div className = "flex flex-wrap gap-4 items-start">
      <Text>Buy</Text>
      <Dropdown
        isJsonWrap={false}
        childList={buyQtyOptions}
        placeholder={ANY_ID}
        updateValue={handleBuyCount}
        isClearable={false}
        value={quantityValue}
        selection="single"
        // @ts-expect-error types
        autoComplete="off"
        style={{width: 84}}
        loading={isLoadingConfig}
        required={isFirstCondition}
        isError={isError}
        errorMessage="Value Invalid"
      />
      <div className={index > 0 ? 'pointer-events-none' : ''}>
        <Dropdown
          isJsonWrap={false}
          childList={buyTypeOptions}
          placeholder={selectedValuesText || Constants.AddProducts}
          value={[...productsValue]}
          updateValue={handleSelectedBuyItem}
          isClearable={true}
          // @ts-expect-error types
          autoComplete="off"
          loading={isLoadingConfig}
          required={isFirstCondition}
          selection="multiple"
          disabled={index > 0}
        />
      </div>
      <Text>and get</Text>
      <RewardItem index={index} promotionCondition={promotionCondition} />

      <PromotionSelectionModal
        key={JSON.stringify(modalProps.type)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          itemType: currentCondition?.itemType || '',
          values: productsValue,
          productCombination: currentCondition?.productCombination,
        }}
      />
    </div>
  )
}
